/** Audit / activity-feed query — tenant-scoped. */

import { keepPreviousData, type UseQueryResult, useQuery } from '@tanstack/react-query';
import { queryKeys } from '../query-keys';
import { useApiClient } from '../react/ApiClientProvider';
import { type AuditLog, auditLogSchema, type Paginated, paginated } from '../responses';
import { unwrap } from './_helpers';

export type AuditFilters = {
  /**
   * Required for hotel-scoped admins; tenant-wide admins may omit it for the
   * whole-tenant feed (the backend enforces this).
   */
  hotelId?: string;
  /** Exact action match. */
  action?: string;
  /** Actor type, e.g. user / ai_actor / system. */
  actorType?: string;
  outcome?: 'success' | 'failure' | 'blocked';
  /** Free-text match against action / resource id. */
  q?: string;
  /** Created at/after (ISO 8601). */
  from?: string;
  /** Created at/before (ISO 8601). */
  to?: string;
  /** Newest-first, default 100, max 500. */
  limit?: number;
  offset?: number;
};

/** `GET /audit` — filtered, searched, paginated, PII-redacted activity feed. */
export function useAuditFeed(
  tenantId: string,
  filters?: AuditFilters,
): UseQueryResult<Paginated<AuditLog>> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.audit(tenantId, filters),
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const result = await client.GET('/api/v1/tenants/{tenantId}/audit', {
        params: {
          path: { tenantId },
          query: {
            hotelId: filters?.hotelId,
            action: filters?.action,
            actorType: filters?.actorType,
            outcome: filters?.outcome,
            q: filters?.q,
            from: filters?.from,
            to: filters?.to,
            limit: filters?.limit,
            offset: filters?.offset,
          },
        },
        parseAs: 'json',
      });
      return unwrap(result, paginated(auditLogSchema));
    },
  });
}
